import { Box, Button, Stack, useTheme } from "@mui/material";
import BusyButton from "src/components/BusyButton";
import { amberCtaSx } from "./amberCtaSx";
import { useT } from "src/i18n";
export default function QuoteNavButtons({
    activeStep, canAdvance, isFinal,
    submitting, nextPending, onBack, onNext, onSubmit,
    isAuthenticated, onOpenLogin, onOpenRegister,
    nextLabel, blockedReason = null,
}) {
    const theme = useTheme();
    const ctaSx = { ...amberCtaSx(theme), paddingX: 3 };
    const amber = theme.custom?.wizard?.amber ?? "#E8893B";
    const t = useT();
    const resolvedNextLabel = nextLabel ?? t("wizard.navButtons.next");

    const backSx = {
        color: "text.secondary",
        textTransform: "none",
        fontWeight: 500,
        letterSpacing: 0,
        paddingLeft: 1,
        "&:hover": {
            backgroundColor: "transparent",
            color: "text.primary",
        },
    };

    // Anonymous on the final step: keep the goal CTA ("Lähetä tarjouspyyntö")
    // visible rather than swapping it for auth buttons. Sending needs an account
    // (contact info) and Gate-1 consent — which only appear once authenticated —
    // so the primary CTA opens the register modal; after sign-in the user returns
    // to step 5 authenticated to accept terms and send. Auth becomes a step inside
    // sending, not a wall in front of it.
    const isAnonFinal = isFinal && !isAuthenticated;

    // Inline text-link style for the secondary "Kirjaudu" affordance.
    const inlineLinkSx = {
        appearance: "none",
        background: "none",
        border: 0,
        p: 0,
        font: "inherit",
        color: amber,
        cursor: "pointer",
        fontWeight: 600,
        borderRadius: "6px",
        "&:hover": { textDecoration: "underline" },
        "&:focus-visible": { outline: `2px solid ${amber}`, outlineOffset: 2 },
    };

    return (
        <Box sx={{ mt: 4 }}>
            {blockedReason && !isAnonFinal && (
                <Box sx={{
                    mb: 1.5,
                    textAlign: { xs: "center", sm: "right" },
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 13,
                    lineHeight: 1.4,
                    color: "text.secondary",
                }}>
                    {blockedReason}
                </Box>
            )}
        <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={2}
            sx={{ justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
            }}
        >
            {activeStep === 0 ? (
                <Box sx={{ display: { xs: "none", sm: "block" } }} />
            ) : (
                <Button
                    variant="text"
                    onClick={onBack}
                    disabled={submitting}
                    sx={{ ...backSx, alignSelf: { xs: "flex-start", sm: "auto" } }}
                >
                    {t("wizard.navButtons.back")}
                </Button>
            )}
            {isAnonFinal ? (
                <Stack
                    spacing={1}
                    sx={{ alignItems: { xs: "stretch", sm: "flex-end" } }}
                >
                    <Button
                        variant="contained"
                        color="warning"
                        size="large"
                        onClick={onOpenRegister}
                        sx={{ ...ctaSx, paddingX: 4, paddingY: 1.5 }}
                    >
                        {t("wizard.navButtons.send")}
                    </Button>
                    <Box sx={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 13,
                        lineHeight: 1.4,
                        color: "text.secondary",
                        textAlign: { xs: "center", sm: "right" },
                    }}>
                        {t("wizard.navButtons.needAccount")}{" "}
                        <Box component="button" type="button" onClick={onOpenLogin} sx={inlineLinkSx}>
                            {t("wizard.navButtons.alreadyHaveAccount")}
                        </Box>
                    </Box>
                </Stack>
            ) : isFinal ? (
                <BusyButton
                    variant="contained"
                    color="warning"
                    size="large"
                    onClick={onSubmit}
                    disabled={!canAdvance}
                    loading={submitting}
                    sx={{ ...ctaSx, paddingX: 4, paddingY: 1.5 }}
                >
                    {submitting ? t("wizard.navButtons.sending") : t("wizard.navButtons.send")}
                </BusyButton>
            ) : (
                <BusyButton
                    variant="contained"
                    color="warning"
                    onClick={onNext}
                    disabled={!canAdvance || submitting}
                    loading={nextPending}
                    sx={ctaSx}
                >
                    {resolvedNextLabel}
                </BusyButton>
            )}
        </Stack>
        </Box>
    );
}
